import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Tabs, Tab, Table, Spinner, Alert, Pagination } from 'react-bootstrap';
import { api } from '../services/api';
import { formatDateRu, formatDateTimeRu } from '../utils/dateFormat';
import { formatSubjectName } from '../utils/formatSubjectName';
import TzProductDetailModal, { TzConfirmedProduct } from './TzProductDetailModal';

const PAGE_SIZE = 20;

export type SubjectDetailProduct = {
  id: number;
  article: string;
  name: string;
  new_wave: boolean;
  created_at?: string | null;
  updated_at?: string | null;
};

export interface SubjectDetailData {
  subjectId: number;
  subjectName: string;
  dateFrom: string;
  dateTo: string;
  inWork: SubjectDetailProduct[];
  statusChanges: SubjectDetailProduct[];
  newProducts: SubjectDetailProduct[];
  tzConfirmed: SubjectDetailProduct[];
}

interface SubjectDetailModalProps {
  show: boolean;
  onHide: () => void;
  subjectId: number | null;
  subjectName: string;
  dateFrom: string;
  dateTo: string;
  userId?: string;
}

function ProductTable({
  products,
  onProductClick
}: {
  products: SubjectDetailProduct[];
  onProductClick: (p: TzConfirmedProduct) => void;
}) {
  if (!products.length) {
    return <p className="text-muted mb-0">Нет товаров.</p>;
  }
  return (
    <Table responsive size="sm" hover>
      <thead>
        <tr>
          <th>Артикул</th>
          <th>Название</th>
          <th>Тип</th>
          <th>Дата добавления</th>
          <th>Дата обновления</th>
        </tr>
      </thead>
      <tbody>
        {products.map((p) => (
          <tr
            key={p.id}
            role="button"
            tabIndex={0}
            style={{ cursor: 'pointer' }}
            onClick={() => onProductClick({ id: p.id, article: p.article, new_wave: p.new_wave })}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onProductClick({ id: p.id, article: p.article, new_wave: p.new_wave });
              }
            }}
          >
            <td>{p.article || '—'}</td>
            <td className="text-break">{p.name || '—'}</td>
            <td>{p.new_wave ? <span className="badge bg-info">New Wave</span> : '—'}</td>
            <td>{formatDateTimeRu(p.created_at)}</td>
            <td>{formatDateTimeRu(p.updated_at)}</td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

function TabContentWithPagination({
  products,
  tabKey,
  tabPage,
  setTabPage,
  onProductClick
}: {
  products: SubjectDetailProduct[];
  tabKey: string;
  tabPage: Record<string, number>;
  setTabPage: React.Dispatch<React.SetStateAction<Record<string, number>>>;
  onProductClick: (p: TzConfirmedProduct) => void;
}) {
  const page = tabPage[tabKey] ?? 1;
  const totalPages = Math.max(1, Math.ceil(products.length / PAGE_SIZE));
  const sliced = useMemo(
    () => products.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [products, page]
  );
  if (!products.length) {
    return <p className="text-muted mb-0">Нет товаров.</p>;
  }
  return (
    <>
      <p className="text-muted small mb-2">
        Показано {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, products.length)} из {products.length}
      </p>
      <ProductTable products={sliced} onProductClick={onProductClick} />
      {totalPages > 1 && (
        <div className="d-flex justify-content-between align-items-center mt-2 flex-wrap gap-2">
          <Pagination size="sm" className="mb-0">
            <Pagination.First disabled={page <= 1} onClick={() => setTabPage((prev) => ({ ...prev, [tabKey]: 1 }))} />
            <Pagination.Prev disabled={page <= 1} onClick={() => setTabPage((prev) => ({ ...prev, [tabKey]: Math.max(1, (prev[tabKey] ?? 1) - 1) }))} />
            <Pagination.Item active>{page} / {totalPages}</Pagination.Item>
            <Pagination.Next disabled={page >= totalPages} onClick={() => setTabPage((prev) => ({ ...prev, [tabKey]: Math.min(totalPages, (prev[tabKey] ?? 1) + 1) }))} />
            <Pagination.Last disabled={page >= totalPages} onClick={() => setTabPage((prev) => ({ ...prev, [tabKey]: totalPages }))} />
          </Pagination>
        </div>
      )}
    </>
  );
}

const SubjectDetailModal: React.FC<SubjectDetailModalProps> = ({
  show,
  onHide,
  subjectId,
  subjectName,
  dateFrom,
  dateTo,
  userId
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<SubjectDetailData | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<TzConfirmedProduct | null>(null);
  const [tabPage, setTabPage] = useState<Record<string, number>>({});

  useEffect(() => {
    if (!show || subjectId == null || !dateFrom || !dateTo) {
      setData(null);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    api
      .getSubjectDetail(subjectId, dateFrom, dateTo, userId)
      .then((res) => {
        if (res.success) {
          setData({
            subjectId: res.subjectId,
            subjectName: res.subjectName,
            dateFrom: res.dateFrom,
            dateTo: res.dateTo,
            inWork: res.inWork || [],
            statusChanges: res.statusChanges || [],
            newProducts: res.newProducts || [],
            tzConfirmed: res.tzConfirmed || []
          });
        } else {
          setData(null);
          setError('Не удалось загрузить данные');
        }
      })
      .catch((e: any) => {
        setData(null);
        setError(e?.response?.data?.error || e?.message || 'Ошибка загрузки');
      })
      .finally(() => setLoading(false));
  }, [show, subjectId, dateFrom, dateTo, userId]);

  const title = subjectName ? `Детализация: ${formatSubjectName(subjectName)}` : 'Детализация по предмету';
  const periodLabel = dateFrom && dateTo ? `Период: ${formatDateRu(dateFrom)} — ${formatDateRu(dateTo)}` : '';

  return (
    <>
      <Modal show={show} onHide={onHide} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>{title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {periodLabel && <p className="text-muted small mb-2">{periodLabel}</p>}
          <p className="text-muted small mb-2">Клик по строке — карточка товара и история (как в «Подготовлено ТЗ по артикулам»).</p>
          {loading && (
            <div className="text-center py-4">
              <Spinner animation="border" />
            </div>
          )}
          {error && <Alert variant="danger">{error}</Alert>}
          {!loading && !error && data && (
            <Tabs defaultActiveKey="inWork" className="mb-2">
              <Tab eventKey="inWork" title={`В работе (${data.inWork.length})`}>
                <TabContentWithPagination
                  products={data.inWork}
                  tabKey="inWork"
                  tabPage={tabPage}
                  setTabPage={setTabPage}
                  onProductClick={setSelectedProduct}
                />
              </Tab>
              <Tab eventKey="statusChanges" title={`Изменение статуса (${data.statusChanges.length})`}>
                <TabContentWithPagination
                  products={data.statusChanges}
                  tabKey="statusChanges"
                  tabPage={tabPage}
                  setTabPage={setTabPage}
                  onProductClick={setSelectedProduct}
                />
              </Tab>
              <Tab eventKey="newProducts" title={`Новые товары (${data.newProducts.length})`}>
                <TabContentWithPagination
                  products={data.newProducts}
                  tabKey="newProducts"
                  tabPage={tabPage}
                  setTabPage={setTabPage}
                  onProductClick={setSelectedProduct}
                />
              </Tab>
              <Tab eventKey="tzConfirmed" title={`Подготовлено ТЗ (${data.tzConfirmed.length})`}>
                <TabContentWithPagination
                  products={data.tzConfirmed}
                  tabKey="tzConfirmed"
                  tabPage={tabPage}
                  setTabPage={setTabPage}
                  onProductClick={setSelectedProduct}
                />
              </Tab>
            </Tabs>
          )}
        </Modal.Body>
      </Modal>
      <TzProductDetailModal
        show={!!selectedProduct}
        onHide={() => setSelectedProduct(null)}
        product={selectedProduct}
      />
    </>
  );
};

export default SubjectDetailModal;
